import { motion } from 'framer-motion';
import { ShieldCheck, ExternalLink, BadgeCheck, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/lib/i18n';

const AUDITOR_URL = 'https://der-ki-auditor.de';
const READINESS_URL = 'https://der-ki-auditor.de/readiness-check';
const CREDLY_42001 = 'https://www.credly.com/badges/29aed2cf-c4b8-45ec-98b4-bffdeedfd959/public_url';
const CREDLY_27001 = 'https://www.credly.com/badges/fb7ada28-8a30-46ec-9782-cf3ecd60d0ad/public_url';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export function KiAuditorBrandBanner() {
  const t = useT();
  const credentials = [
    {
      title: 'ISO/IEC 42001',
      role: 'Senior Lead Auditor',
      issuer: t('PECB · Credly verifiziert', 'PECB · Credly verified'),
      url: CREDLY_42001,
    },
    {
      title: 'ISO/IEC 27001',
      role: 'Lead Auditor',
      issuer: t('PECB · Credly verifiziert', 'PECB · Credly verified'),
      url: CREDLY_27001,
    },
  ] as const;
  return (
    <section
      aria-label={t('Schwestermarke DER-KI-AUDITOR', 'Sister brand DER-KI-AUDITOR')}
      className="relative py-16 md:py-24 border-y border-primary/20 bg-secondary/40 overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern opacity-10" aria-hidden="true" />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-mono uppercase tracking-wider text-primary">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              {t('Schwestermarke · ISO 42001 · ISO 27001 · EU AI Act', 'Sister brand · ISO 42001 · ISO 27001 · EU AI Act')}
            </span>

            <div className="mt-6 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-7 h-7 text-primary" aria-hidden="true" />
              </div>
              <div className="font-mono text-2xl sm:text-3xl font-bold tracking-tight leading-none">
                <span className="text-foreground">DER </span>
                <span className="text-primary">KI-AUDITOR</span>
              </div>
            </div>

            <h2 className="mt-6 text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              {t('Für formale KI-Audits haben wir eine ', 'For formal AI audits we operate a ')}
              <span className="text-primary">{t('eigene Marke', 'dedicated brand')}</span>.
            </h2>

            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t(
                'FERNAU Consulting begleitet Sie bei Strategie, Implementierung und Betrieb Ihrer KI- und Digitalisierungsprojekte. Für unabhängige, formale Audits nach ISO/IEC 42001 und ISO/IEC 27001 sowie für die strukturierte Vorbereitung auf den EU AI Act führen wir bewusst eine separate Marke: DER-KI-AUDITOR. So bleiben Beratung und Audit sauber getrennt, wie es professionelle Auditpraxis verlangt.',
                'FERNAU Consulting guides you through strategy, implementation and operation of your AI and digitalization projects. For independent, formal audits to ISO/IEC 42001 and ISO/IEC 27001, and for structured EU AI Act readiness, we deliberately operate a separate brand: DER-KI-AUDITOR. Consulting and audit stay cleanly separated, as professional audit practice requires.',
              )}
            </p>

            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {t(
                'Hinter beiden Marken steht Lars Zimmermann: PECB Senior Lead Auditor ISO/IEC 42001 und Lead Auditor ISO/IEC 27001, mit 20 Jahren Industrie- und Führungserfahrung im produzierenden Mittelstand. Klartext aus der Praxis statt Berater-Bla-Bla.',
                'Both brands are led by Lars Zimmermann: PECB Senior Lead Auditor ISO/IEC 42001 and Lead Auditor ISO/IEC 27001, with 20 years of industry and leadership experience in mid-sized manufacturing. Plain talk from practice — no consultant blabla.',
              )}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button variant="hero" size="lg" asChild>
                <a
                  href={AUDITOR_URL}
                  target="_blank"
                  rel="noopener noreferrer external"
                  aria-label={t(
                    'Zur Website von DER-KI-AUDITOR (öffnet in neuem Tab)',
                    'Visit DER-KI-AUDITOR website (opens in a new tab)',
                  )}
                >
                  {t('Zur Auditor-Website', 'Visit auditor website')}
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </a>
              </Button>
            </div>

            <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/15">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <GraduationCap className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <div className="text-sm font-semibold">PECB Authorized Training Partner</div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                  {t(
                    'Unter der Marke DER-KI-AUDITOR bieten wir als authorized Partner von PECB international anerkannte Weiterbildungsprogramme zu ISO 42001, ISO 27001 und dem EU AI Act an.',
                    'Under the DER-KI-AUDITOR brand we deliver internationally recognized PECB training programs (Authorized Training Partner) for ISO 42001, ISO 27001 and the EU AI Act.',
                  )}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4"
          >
            {credentials.map((cred) => (
              <a
                key={cred.title}
                href={cred.url}
                target="_blank"
                rel="noopener noreferrer external"
                className="group p-5 rounded-2xl bg-background/60 backdrop-blur border border-border/60 hover:border-primary/40 transition-colors"
                aria-label={t(
                  `${cred.title} ${cred.role} – Credly Verifizierung öffnen`,
                  `${cred.title} ${cred.role} – open Credly verification`,
                )}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <BadgeCheck className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      PECB
                    </div>
                    <div className="mt-0.5 font-bold text-base sm:text-lg leading-tight">
                      {cred.title}
                    </div>
                    <div className="text-sm text-primary font-medium">{cred.role}</div>
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                      {cred.issuer}
                      <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}